import { isFinalErasure, type ErasureReceipt } from '../../services/api'

/**
 * How a receipt READS — the whole of PR 2's judgment, in one place that can be driven
 * directly (App IA v5 phase 5b, MAR-204 PR 2).
 *
 * ── The trap this module exists to hold ─────────────────────────────────────────────────
 *
 * The receipt states `terminal`, and **`terminal` is a superset of finality**:
 * `TERMINAL_ERASURE_STATUS` is `['completed','needs_review','failed']`, but `failed` may be
 * reopened — the engine throws so Lambda retries, and `markErasureRunning` /
 * `markErasureTerminal` both exclude `failed` from their "already finished" condition
 * precisely so a retry can. So `terminal` answers *stop polling*, never *this is over*.
 * Reading it as done would tell a merchant their customer is forgotten while the platform
 * is still trying.
 *
 * ⚠️ **But the converse is equally wrong, and is the subtler half.** A `failed` may also be
 * over: the retries are capped at 2, and the door writes `failed` for a job it never
 * dispatched. So `failed` means *neither* "finished" *nor* "still going", and no reading
 * here may claim either. `readingOf` below carries the full three-way; the surface's cover
 * for the case it cannot name is to return the erase control on any terminal `failed`.
 *
 * ── And the claim it must not break in either direction ─────────────────────────────────
 *
 * `needs_review` is **terminal and is not a failure**. Everything provably owned IS
 * deleted; what is withheld is the *proof* that the sweep was exhaustive. Since MAR-195 a
 * row without a `marvaVisitorIds` ledger is normally proven from the link table's
 * `LinkTargetIndex`, so `visitor_roster_unproven` needs that search to have failed too —
 * rarer, but still a state this surface must render honestly.
 *
 *   - Rendering it as a **failure** tells a merchant the erasure did not happen. It did.
 *   - Rendering it as an unqualified **"Forgotten"** launders a withheld residual into a
 *     completeness claim the receipt explicitly refuses to make — which is the exact
 *     laundering `ERASURE_STATUS` was given its own enum member to prevent.
 *
 * So the qualification goes in the HEADLINE, not in small print underneath it.
 *
 * ── One exception inside `needs_review`, and it is a real one ───────────────────────────
 *
 * `pii_decrypt_failed` is not "the proof is thin". `stripAttribution`,
 * `stripOutboundMessages`, `stripRecoveryEmails` and `deleteCustomerInteractions` all match
 * on a PLAINTEXT email or phone, so without readable anchors those four steps swept
 * nothing. That is a certain gap in the **deletion**, and saying "forgotten, proof
 * incomplete" over it would be the softer of two true-sounding sentences and the wrong one.
 * Same for a subject the engine could not tie to the request at all.
 *
 * ── Copy lives here, not in JSX ─────────────────────────────────────────────────────────
 *
 * The only reason the sentences sit in a module instead of in the component is that the
 * cases that matter most are the ones the screen is slowest to reach: a `needs_review`
 * carrying an unverified merge edge, a `failed` on its third attempt. Driving them through
 * a render is possible and is done, but the exhaustive pass over the vocabulary is a table
 * test against these functions.
 */

/** The three ways a settled receipt reads, plus the two it reads before it settles. */
export type ErasureReading =
  /** Dispatched; the engine has not reported. */
  | 'working'
  /** We stopped watching. The work did not stop. */
  | 'unwatched'
  /** `completed` — deleted, and she can account for all of it. */
  | 'forgotten'
  /** `needs_review` — deleted, and she cannot prove it was everything. */
  | 'forgotten_unproven'
  /** `needs_review` where a step certainly did NOT run. Deletion itself is short. */
  | 'forgotten_partial'
  /** `failed` before anything was dispatched. No job exists; nothing was changed. */
  | 'not_started'
  /** `failed` — the last attempt broke. It is NOT done, and it may or may not resume. */
  | 'retrying'

/**
 * `UNRESOLVED.*` tokens whose meaning is "a step could not run", as opposed to "she could
 * not prove the sweep reached everything".
 *
 * ⚠️ A token NOT in this set is not assumed benign — it falls to `forgotten_unproven`,
 * which still qualifies its headline. The set exists to make a stronger statement about
 * three tokens, never to make a weaker one about the rest.
 */
const DELETION_SHORT_REASONS: ReadonlySet<string> = new Set([
  // The subject's own row holds a contact we could not read back, so the four
  // plaintext-matching steps swept nothing.
  'pii_decrypt_failed',
  // The engine could not tie the request to a data subject at all.
  'contact_hash_match_unproven',
  'no_shopify_customer_id',
])

/**
 * The step name the DOOR writes when the async invoke itself threw.
 *
 * ⚠️ It is not an engine step. `redactCustomer`'s report entries are table names
 * (`conversations`, `observations`, …); `dispatch` is written in exactly one place —
 * `knowledge-api/src/erasure.js`'s catch around `InvokeCommand` — so its presence is a
 * reliable statement that **the job was never handed over**.
 */
const DISPATCH_STEP = 'dispatch'

/** Did this run fail before anything was dispatched? Then nothing was changed. */
function neverDispatched(receipt: ErasureReceipt): boolean {
  return (receipt.stores || []).some((s) => s.store === DISPATCH_STEP && s.ok === false)
}

/**
 * How does this receipt read?
 *
 * ⚠️ The `failed` test comes BEFORE the terminal test on purpose, and `needsReview` is read
 * off the server's own boolean rather than re-derived from the status string. Both are the
 * same defence: the two facts a surface is most likely to get backwards should not be
 * expressed as conditions a reader has to reconstruct.
 *
 * ── ⚠️ `failed` is THREE situations, and only one of them retries ───────────────────────
 *
 * "The engine throws so Lambda retries" is true of one path and false of two:
 *
 *  1. **Never dispatched.** `InvokeCommand` threw in the door, which wrote
 *     `stores: [{ store: 'dispatch', ok: false }]` and answered 500 *"Nothing has been
 *     changed."* **No job exists**, so nothing will ever retry it. Discriminable, and told
 *     apart here — "nothing was changed at all" is a materially different thing to say to a
 *     merchant than "it broke part way through".
 *  2. **Retries exhausted.** `maximum_retry_attempts = 2` on the async invoke
 *     (`lambda.tf`). After those, `failed` is genuinely final. **Not reliably
 *     discriminable** — `attempts` is suggestive, not decisive.
 *  3. **Between retries.** Genuinely still going.
 *
 * Because 2 and 3 cannot be told apart, the `retrying` copy claims neither, and **the
 * surface must not depend on telling them apart**: `CustomerErasure` returns the erase
 * control on *any* terminal `failed`, which covers the case that cannot be named. A reading
 * that guessed and then stranded the merchant would be worse than one that says "this did
 * not finish — start it again".
 *
 * @param receipt the receipt as `toReceiptView` projected it
 * @param unwatched true when this client gave up polling — see `CustomerErasure`
 */
export function readingOf(receipt: ErasureReceipt, unwatched = false): ErasureReading {
  if (receipt.status === 'failed') return neverDispatched(receipt) ? 'not_started' : 'retrying'
  if (!isFinalErasure(receipt)) return unwatched ? 'unwatched' : 'working'
  if (receipt.needsReview) {
    const reason = receipt.resolutionReason
    if (typeof reason === 'string' && DELETION_SHORT_REASONS.has(reason)) return 'forgotten_partial'
    // A run that could not find the subject deleted nothing of theirs, whatever else it says.
    if (receipt.customerFound === false) return 'forgotten_partial'
    return 'forgotten_unproven'
  }
  return 'forgotten'
}

/** The headline and the sentence under it, per reading. */
export interface ErasureCopy {
  headline: string
  lead: string
}

/**
 * ⚠️ **Not one unconditional string.** The v6 design fires a single toast — *"Erased in one
 * action — every observation, every inference, and the conversations behind them"* — the
 * moment the button is pressed. That is a completeness claim made before the engine has
 * reported, and the API's most common answer cannot support it. These five can.
 *
 * @param reading from `readingOf`
 * @param who the masked contact, or another name for the record; `null` when there is none
 */
export function copyFor(reading: ErasureReading, who: string | null): ErasureCopy {
  // `she` rather than a name when the record has no readable contact point. Never a
  // fabricated one, and never a bare "this customer" where a mask exists — a confirmation
  // and a result that cannot distinguish two people say nothing about either.
  const subject = who ? `everything she held about ${who}` : 'everything she held about this person'
  switch (reading) {
    case 'working':
      return {
        headline: 'Erasing…',
        lead: 'Marva is working through every place she has ever written about this person. This normally takes under a minute.',
      }
    case 'unwatched':
      return {
        headline: 'Still running.',
        lead: 'This is taking longer than this panel waits. The work does not stop because the window stopped watching — it finishes on our side, and the receipt is where it lands. Check again whenever you like.',
      }
    case 'forgotten':
      return {
        headline: 'Forgotten.',
        lead: `In one action, ${subject} is gone — every observation, every inference, and the conversations behind them. She can account for all of it.`,
      }
    case 'forgotten_unproven':
      // ⚠️ The qualification is IN THE HEADLINE. Putting it in a footnote is what makes a
      // withheld residual read as a clean erasure, which is the thing `needs_review` exists
      // as its own status to stop.
      return {
        headline: 'Forgotten — and she cannot prove it was everything.',
        lead: `${subject[0].toUpperCase()}${subject.slice(1)} that she could prove was hers is deleted: the observations, the inferences and the conversations behind them. What she cannot prove is that she reached all of it. This is not a failure and there is nothing for you to do — it is the part she will not claim.`,
      }
    case 'forgotten_partial':
      return {
        headline: 'Forgotten in part — some of it did not run.',
        lead: 'Marva deleted everything she could reach, but at least one step could not run at all, so this erasure is not complete. That is ours to fix, not yours, and a record of it is with us.',
      }
    case 'not_started':
      // The one `failed` we can name. Nothing was dispatched, so nothing was touched —
      // and that is the reassurance a merchant most needs from a failed erasure.
      return {
        headline: 'It never started.',
        lead: 'Marva could not hand this erasure to the part of the system that carries it out, so nothing was changed at all — nothing deleted, and nothing half-deleted. Starting it again is the whole fix.',
      }
    case 'retrying':
      // ⚠️ Not one word that could be read as done — `failed` is terminal in the enum. But
      // equally not a promise that it WILL finish: `maximum_retry_attempts = 2`, and a
      // receipt cannot say whether those are spent. So this claims neither, and the control
      // to start again is offered alongside it. Saying "she retries, so this has not
      // stopped" — which an earlier cut did — strands a merchant on an erasure nothing is
      // going to pick up.
      return {
        headline: 'Not finished.',
        lead: 'The last attempt broke part way through. Marva retries an erasure that fails, but not forever — so this may still finish on its own, and it may not. Check again in a moment; if it stays here, start it again. Asking twice cannot take anything extra away, because the work is repeatable.',
      }
  }
}

/**
 * What she set aside, in the merchant's words — one line each.
 *
 * ⚠️ Built from the RESIDUAL and the reason together, deduplicated by cause. The residual
 * carries every count; `resolutionReason` carries only the single highest-severity one, in
 * the engine's own order — so reading either alone under-reports.
 *
 * ⚠️ `otherAnchor` is never listed. Those rows were proven to belong to somebody else, so
 * withholding them misses nothing — `ERASURE_WITHHELD_BENIGN_COUNTERS` names them and
 * `erasureReceiptWithheldSomething` excludes them for the same reason. Listing them would
 * report other people's data as this person's residue.
 *
 * ⚠️ An unrecognised `resolutionReason` produces a true, generic line rather than nothing.
 * A `needs_review` with no explanation underneath it is the laundering again, one level out:
 * the qualification in the headline would look like boilerplate with no cause.
 */
export function withheldNotes(receipt: ErasureReceipt): string[] {
  const residual = receipt.residual || {}
  const reason = typeof receipt.resolutionReason === 'string' ? receipt.resolutionReason : null
  const notes = new Map<string, string>()
  const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

  if (residual.visitorRosterProven === false || reason === 'visitor_roster_unproven') {
    notes.set('roster', 'This person\'s record never listed the browsers they used, so Marva swept the ones she knew of rather than a list she could vouch for.')
  }
  if (residual.locatorLedgerRetained === true) {
    notes.set('ledger', 'The file that maps browsers back to people is kept, because it is the only route to anything that list could not name.')
  }

  const edges = residual.unverifiedMergeEdges
  if ((typeof edges === 'number' && edges > 0) || reason === 'unverified_merge_edge') {
    const n = typeof edges === 'number' && edges > 0 ? edges : null
    notes.set('merge', n
      ? `${n} other customer ${plural(n, 'record is', 'records are')} linked to this one by a merge Marva cannot verify. Nothing of ${plural(n, 'theirs', 'theirs')} was touched.`
      : 'Another customer record is linked to this one by a merge Marva cannot verify. Nothing of theirs was touched.')
  }

  const conv = residual.withheldConversations || {}
  if ((conv.ambiguous || 0) > 0 || reason === 'conversation_anchor_conflict') {
    const n = conv.ambiguous || 0
    notes.set('conv_ambiguous', n > 0
      ? `${n} ${plural(n, 'conversation names', 'conversations name')} two different people at once, and ${plural(n, 'was', 'were')} left alone rather than deleted on a guess.`
      : 'Some conversations name two different people at once, and were left alone rather than deleted on a guess.')
  }
  if ((conv.unanchored || 0) > 0 || reason === 'unanchored_conversations') {
    const n = conv.unanchored || 0
    notes.set('conv_unanchored', n > 0
      ? `${n} ${plural(n, 'conversation names', 'conversations name')} nobody at all, so Marva has no way to prove ${plural(n, 'it was', 'they were')} this person's.`
      : 'Some conversations name nobody at all, so Marva has no way to prove they were this person\'s.')
  }

  const links = residual.withheldIdentityLinks || {}
  if ((links.ambiguous || 0) > 0 || reason === 'identity_link_anchor_conflict') {
    const n = links.ambiguous || 0
    notes.set('link_ambiguous', n > 0
      ? `${n} identity ${plural(n, 'link names', 'links name')} two different people at once, and ${plural(n, 'was', 'were')} kept rather than cut on a guess.`
      : 'Some identity links name two different people at once, and were kept rather than cut on a guess.')
  }
  if ((links.unanchored || 0) > 0) {
    const n = links.unanchored || 0
    notes.set('link_unanchored', `${n} identity ${plural(n, 'link names', 'links name')} nobody at all, so Marva cannot prove ${plural(n, 'it is', 'they are')} this person's.`)
  }

  if (reason === 'pii_decrypt_failed') {
    notes.set('pii', 'Marva holds contact details for this person that she could not read back. Four of the steps match on an email address or a phone number, so those four swept nothing. That is a gap in the deletion itself, and the cause is ours — a key, not anything about this person.')
  }
  if (reason === 'contact_hash_match_unproven') {
    notes.set('subject', 'Records matched on a contact detail alone and none of them names this customer, so Marva would not destroy them on the strength of the match.')
  }
  if (reason === 'no_shopify_customer_id') {
    notes.set('subject', 'The request reached the engine without an id it could prove anything against.')
  }

  for (const step of receipt.failedSteps || []) {
    // ⚠️ `dispatch` is skipped: it is the DOOR's marker, not an engine step, and the
    // `not_started` copy already says the whole of what it means. Listing it would put
    // "One step did not finish: dispatch" under a headline that has just explained that
    // nothing ran at all.
    if (step === DISPATCH_STEP) continue
    notes.set(`step:${step}`, `One step did not finish: ${step}.`)
  }

  if (notes.size === 0 && (receipt.needsReview || reason)) {
    // ⚠️ Never silence. A reason we do not have copy for still has to say that something
    // was set aside — degrading to the server's own token would show a merchant
    // `visitor_roster_unproven`, and degrading to nothing would leave a qualified headline
    // with no cause under it.
    notes.set('unknown', 'Marva set something aside that she could not prove belonged to this person, and flagged it for us to look at.')
  }

  return [...notes.values()]
}

/**
 * The one-line status word for the history list.
 *
 * ⚠️ Derived from `readingOf`, not from `status`, so the list and the detail cannot drift
 * into disagreeing about whether `failed` is over.
 */
export function historyWord(receipt: ErasureReceipt): string {
  switch (readingOf(receipt)) {
    case 'forgotten': return 'Forgotten'
    case 'forgotten_unproven': return 'Forgotten, not proven complete'
    case 'forgotten_partial': return 'Forgotten in part'
    // ⚠️ Neither of these may read as over OR as guaranteed to resume — see `readingOf`.
    case 'not_started': return 'Never started'
    case 'retrying': return 'Not finished'
    default: return 'Running'
  }
}
